import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { profile as profileApi, UserProfile } from '../lib/api'

function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="text-2xl font-bold text-gray-100">{value}</div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
    </div>
  )
}

export default function ProfilePage() {
  const { userId } = useParams<{ userId?: string }>()
  const [data, setData] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    const fetch = userId ? profileApi.get(userId) : profileApi.me()
    fetch
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load profile'))
      .finally(() => setLoading(false))
  }, [userId])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-400">Loading profile...</div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-red-400">{error || 'No profile data'}</div>
      </div>
    )
  }

  const { user, track_record, ideas, recent_resolutions } = data
  const isAgent = user.is_agent === true

  // Sort ideas by resolved count for the breakdown
  const sortedIdeas = Object.entries(track_record.todos_by_idea)
    .sort(([, a], [, b]) => b - a)

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-brand-600 flex items-center justify-center text-2xl font-bold text-white">
            {isAgent ? 'A' : user.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">
              {isAgent ? 'Agent' : user.email}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-400 capitalize">{user.role}</span>
              {isAgent && (
                <span className="text-xs bg-brand-900/50 text-brand-300 px-2 py-0.5 rounded-full border border-brand-700">
                  autonomous
                </span>
              )}
              {data.member_since && (
                <span className="text-sm text-gray-500">
                  Joined {new Date(data.member_since).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Track Record Stats */}
        <div>
          <h2 className="text-lg font-semibold text-gray-200 mb-3">Track Record</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="TODOs Resolved" value={track_record.todos_resolved} />
            <StatCard label="Ideas" value={track_record.ideas_created} />
            <StatCard label="Conversations" value={track_record.conversations} />
            <StatCard label="Comments" value={track_record.comments} />
          </div>
        </div>

        {/* TODOs by Idea Breakdown */}
        {sortedIdeas.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Resolutions by Idea</h2>
            <div className="space-y-2">
              {sortedIdeas.map(([idea, count]) => {
                const pct = track_record.todos_resolved > 0
                  ? Math.round((count / track_record.todos_resolved) * 100)
                  : 0
                return (
                  <div key={idea} className="flex items-center gap-3">
                    <span className="text-sm text-gray-300 w-40 truncate" title={idea}>{idea}</span>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-400 w-16 text-right">{count}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Ideas */}
        {ideas.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Ideas</h2>
            <div className="space-y-2">
              {ideas.map(idea => (
                <div key={idea.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-gray-300">{idea.title}</span>
                  <span className="text-xs text-gray-500">{relativeTime(idea.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Resolutions */}
        {recent_resolutions.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Recent Resolutions</h2>
            <div className="space-y-3">
              {recent_resolutions.map(todo => (
                <div key={todo.todo_id} className="border-b border-gray-800 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-gray-300 flex-1">{todo.question}</p>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {relativeTime(todo.addressed_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {todo.idea && (
                      <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded">
                        {todo.idea}
                      </span>
                    )}
                    {todo.todo_type && (
                      <span className="text-xs text-gray-500">{todo.todo_type}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agent State (agent users only) */}
        {isAgent && data.agent_state && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Agent State</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(data.agent_state as Record<string, unknown>)
                .filter(([, v]) => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean')
                .map(([key, value]) => (
                  <div key={key} className="bg-gray-800 rounded p-3">
                    <div className="text-sm font-medium text-gray-100">{String(value)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{key.replace(/_/g, ' ')}</div>
                  </div>
                ))}
            </div>
            {/* Nested objects as collapsible JSON */}
            {Object.entries(data.agent_state as Record<string, unknown>)
              .filter(([, v]) => typeof v === 'object' && v !== null)
              .map(([key, value]) => (
                <details key={key} className="mt-3">
                  <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300">
                    {key.replace(/_/g, ' ')}
                  </summary>
                  <pre className="mt-2 text-xs text-gray-500 bg-gray-800 rounded p-3 overflow-x-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </details>
              ))}
          </div>
        )}

        {/* Agent Audit Log (agent users only) */}
        {isAgent && data.agent_audit && data.agent_audit.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-200 mb-4">Recent API Actions</h2>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {data.agent_audit.map((entry, i) => (
                <div key={i} className="flex items-center gap-3 py-1 text-xs">
                  <span className={`font-mono ${
                    entry.policy_result === 'ALLOWED' ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {entry.policy_result === 'ALLOWED' ? 'OK' : 'BL'}
                  </span>
                  <span className="text-gray-500 font-mono w-12">{entry.method}</span>
                  <span className="text-gray-400 flex-1 truncate">{entry.path}</span>
                  <span className="text-gray-600">{relativeTime(entry.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
